import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ChevronDown, FileText, FolderOpen, GitCompare, Loader2, Lock, TriangleAlert, Upload, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useI18n } from "@/i18n";
import { cn } from "@/lib/utils";
import { ApiResponse } from "../../http-client/Response";
import { Comparison, ComparisonListItem, listComparisons, startComparison } from "../../services/Comparisons";
import { STANDARD_AGREEMENT_ACCEPT, uploadStandardAgreement } from "../../services/StandardAgreements";
import { listWorkflows, Workflow } from "../../services/Workflows";
import {
  AUTO_ROLE,
  fileProblem,
  isFailed,
  isReady,
  lastRoles,
  nameFromFileName,
  ROLES,
  standardWithFileName,
  startErrorMessage,
  suggestStandard,
  useFileDrop,
} from "./compare-utils";
import ProjectFilePicker, { PickedFile } from "./ProjectFilePicker";
import QuickCheckCard, { QuickCheck, workflowFocus } from "./QuickCheckCard";
import { RISK_CHIP } from "./risk-styles";
import StandardsLibrary from "./StandardsLibrary";
import { RetryButton, StandardChip } from "./StandardStatus";
import { useStandards } from "./useStandards";

const formatSize = (bytes: number) =>
  bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;

const INITIAL_ROWS = 8;

type Source = { kind: "upload"; file: File } | { kind: "project"; file: PickedFile };

const StepCard: React.FC<{
  n: number;
  title: string;
  hint?: string;
  children: React.ReactNode;
}> = ({ n, title, hint, children }) => (
  <Card className="space-y-4 p-4 sm:p-6" data-testid={`step-${n}`}>
    <div className="flex items-start gap-3">
      <span
        className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand text-sm font-semibold text-white"
        aria-hidden
      >
        {n}
      </span>
      <div className="min-w-0">
        <h2 className="text-lg font-semibold leading-8">{title}</h2>
        {hint && <p className="-mt-1 text-sm text-muted-foreground">{hint}</p>}
      </div>
    </div>
    {children}
  </Card>
);

/**
 * Compare a document with one of the organization's standard agreements, in
 * three steps: our standard, the agreement we received, our role. Saved compare
 * workflows run in one drop, the person's own earlier comparisons are listed
 * below, and the "Our standards" tab manages the standards themselves.
 */
const ComparePage: React.FC = () => {
  const { t, locale } = useI18n();
  const c = t.comparisons;
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const fileInput = useRef<HTMLInputElement>(null);
  const standardInput = useRef<HTMLInputElement>(null);

  const { standards, reload: reloadStandards } = useStandards();
  const view = params.get("view") === "standards" ? "standards" : "compare";
  const setView = (next: string) =>
    setParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        if (next === "standards") p.set("view", "standards");
        else p.delete("view");
        return p;
      },
      { replace: false },
    );
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [items, setItems] = useState<ComparisonListItem[] | null>(null);

  const [source, setSource] = useState<Source | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  // What the person (or a link) chose; otherwise the suggestion applies
  const [chosenStandard, setChosenStandard] = useState<string | null>(params.get("standard"));
  const [chosenRole, setChosenRole] = useState<string | null>(params.get("role"));
  const [focus, setFocus] = useState(params.get("focus") ?? "");
  const [optionsOpen, setOptionsOpen] = useState(!!params.get("focus"));
  const [busy, setBusy] = useState<string | null>(null);
  const [uploadingStandard, setUploadingStandard] = useState(false);
  // The file name the person chose to compare anyway although it looks like a standard
  const [ignoredLookalike, setIgnoredLookalike] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);

  const language = locale === "sv" ? "sv" : "en";

  useEffect(() => {
    let cancelled = false;
    listWorkflows()
      .then((res) => !cancelled && setWorkflows(res.success && res.data ? res.data.filter((w) => w.kind === "compare") : []))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadItems = useCallback(async () => {
    try {
      const res = await listComparisons();
      if (res.success && res.data) setItems(res.data);
      else setItems((prev) => prev ?? []);
    } catch {
      setItems((prev) => prev ?? []);
    }
  }, []);
  useEffect(() => {
    void loadItems();
  }, [loadItems]);

  // Keep running comparisons fresh
  const anyRunning = !!items?.some((i) => i.status === "queued" || i.status === "processing");
  useEffect(() => {
    if (!anyRunning) return;
    const timer = setInterval(() => void loadItems(), 4000);
    return () => clearInterval(timer);
  }, [anyRunning, loadItems]);

  const all = useMemo(() => standards ?? [], [standards]);
  const ready = useMemo(() => all.filter(isReady), [all]);

  const fileName = source?.file.name ?? null;
  const suggestedId = useMemo(() => suggestStandard(fileName, ready, items ?? []), [fileName, ready, items]);
  const remembered = useMemo(() => lastRoles(items ?? []), [items]);

  // A chosen standard stays chosen while it is prepared, and is used the moment it is ready
  const chosen = all.find((x) => x.id === chosenStandard) ?? null;
  const selected = chosen ?? all.find((x) => x.id === suggestedId) ?? null;
  // The selected standard shows its own status in the picker; list the others, and a failed one with its Retry
  const notReady = all.filter((x) => !isReady(x) && (x.id !== selected?.id || isFailed(x)));
  const standardId = selected && isReady(selected) ? selected.id : "";
  const role = chosenRole ?? remembered.get(standardId) ?? AUTO_ROLE;

  const lookalike = source && source.file.name !== ignoredLookalike ? standardWithFileName(source.file.name, all) : null;

  // Why Compare is disabled, next to the button
  const reason =
    selected && !isReady(selected)
      ? isFailed(selected)
        ? c.cp.reasonFailed(selected.name)
        : c.cp.reasonWaiting(selected.name)
      : !standardId
        ? c.cp.reasonChoose
        : !source
          ? c.cp.reasonFile
          : null;

  const uploadStandard = async (file: File): Promise<boolean> => {
    const problem = fileProblem(file, c);
    if (problem) {
      toast.error(problem);
      return false;
    }
    setUploadingStandard(true);
    try {
      const res = await uploadStandardAgreement(file, {
        name: nameFromFileName(file.name),
      });
      if (res.success && res.data) {
        // Selected now; usable as soon as it is prepared
        setChosenStandard(res.data.id);
        await reloadStandards(true);
        return true;
      }
      toast.error(res.status === 413 ? c.tooLarge : c.lib.uploadFailed);
    } catch {
      toast.error(c.lib.uploadFailed);
    } finally {
      setUploadingStandard(false);
    }
    return false;
  };

  const take = useCallback(
    (file: File) => {
      const problem = fileProblem(file, c);
      if (problem) return void toast.error(problem);
      setSource({ kind: "upload", file });
      setIgnoredLookalike(null);
    },
    [c],
  );
  const { dragging, handlers } = useFileDrop(take, !!busy);
  const choose = () => fileInput.current?.click();

  const finish = (res: ApiResponse<Comparison>) => {
    if (res.success && res.data) navigate(`/comparisons/${res.data.id}`);
    else toast.error(startErrorMessage(res, c));
  };

  const run = async () => {
    if (!source || !standardId || busy) return;
    setBusy("run");
    try {
      finish(
        await startComparison({
          standardAgreementId: standardId,
          ...(source.kind === "upload" ? { file: source.file } : { receivedFileId: source.file.id }),
          ourRole: role === AUTO_ROLE ? undefined : role,
          focus,
          language,
        }),
      );
    } catch {
      toast.error(c.startFailed);
    } finally {
      setBusy(null);
    }
  };

  const runQuickCheck = async (check: QuickCheck, file: File) => {
    if (busy || !check.standard) return;
    setBusy(check.workflow.id);
    try {
      finish(
        await startComparison({
          standardAgreementId: check.standard.id,
          file,
          // The role last used with this standard, if any
          ourRole: remembered.get(check.standard.id),
          focus: workflowFocus(check.workflow),
          language,
        }),
      );
    } catch {
      toast.error(c.startFailed);
    } finally {
      setBusy(null);
    }
  };

  const quickChecks: QuickCheck[] = workflows.map((workflow) => ({
    workflow,
    standard: (standards ?? []).find((s) => s.id === workflow.standardAgreementId) ?? null,
  }));

  const dateTime = (iso: string) =>
    new Date(iso).toLocaleString(locale === "sv" ? "sv-SE" : "en-GB", {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });

  const visibleItems = showAll ? (items ?? []) : (items ?? []).slice(0, INITIAL_ROWS);

  return (
    <div className="mx-auto w-full max-w-4xl space-y-10 p-4 sm:p-8">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">{c.cp.title}</h1>
        <p className="mt-1 text-muted-foreground">{c.cp.subtitle}</p>
      </div>

      <Tabs value={view} onValueChange={setView}>
        <TabsList data-testid="compare-tabs">
          <TabsTrigger value="compare">{c.cp.tabCompare}</TabsTrigger>
          <TabsTrigger value="standards">{c.cp.tabStandards}</TabsTrigger>
        </TabsList>
      </Tabs>

      {standards == null ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : view === "standards" ? (
        <StandardsLibrary
          standards={all}
          reload={() => reloadStandards(true)}
          onCompare={(id) => {
            setChosenStandard(id);
            setView("compare");
          }}
        />
      ) : (
        <section aria-label={c.cp.title} className="space-y-4">
          {/* 1. Our standard */}
          <StepCard n={1} title={c.cp.step1Title} hint={all.length ? c.cp.step1Hint : undefined}>
            {all.length === 0 ? (
              <div className="flex flex-col items-start gap-3" data-testid="no-standards">
                <p className="text-sm text-muted-foreground">{c.cp.standardsEmpty}</p>
                <Button onClick={() => standardInput.current?.click()} disabled={uploadingStandard}>
                  {uploadingStandard ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
                  {uploadingStandard ? c.cp.uploadingStandard : c.cp.uploadFirst}
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                  <div className="min-w-0 sm:flex-1">
                    <Select value={selected?.id ?? ""} onValueChange={setChosenStandard}>
                      <SelectTrigger id="cmp-standard" aria-label={c.cp.standardsLabel} data-testid="standard-select">
                        <SelectValue placeholder={c.cp.chooseStandard}>
                          {selected && (
                            <span className="flex min-w-0 items-center gap-2">
                              <span className="truncate">{selected.name}</span>
                              <StandardChip standard={selected} />
                            </span>
                          )}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent className="max-w-[calc(100vw-2rem)]">
                        {all.map((x) => (
                          <SelectItem key={x.id} value={x.id} disabled={!isReady(x)}>
                            <span className="flex flex-wrap items-center gap-x-2 gap-y-1 whitespace-normal">
                              <span className="break-words">
                                {x.name}
                                {x.id === suggestedId && !chosen && isReady(x) ? ` (${c.cp.suggested})` : ""}
                              </span>
                              <StandardChip standard={x} />
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => standardInput.current?.click()}
                    disabled={uploadingStandard}
                    data-testid="upload-standard"
                  >
                    {uploadingStandard ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
                    {uploadingStandard ? c.cp.uploadingStandard : c.cp.uploadNew}
                  </Button>
                </div>
                {notReady.length > 0 && (
                  <ul className="space-y-1.5" aria-live="polite" data-testid="standards-not-ready">
                    {notReady.map((x) => (
                      <li key={x.id} className="flex flex-wrap items-center gap-2 text-sm">
                        <span className="min-w-0 break-words">{x.name}</span>
                        <StandardChip standard={x} />
                        {isFailed(x) && <RetryButton standard={x} onDone={() => reloadStandards(true)} />}
                      </li>
                    ))}
                  </ul>
                )}
                <Button variant="link" size="sm" className="h-auto p-0" onClick={() => setView("standards")} data-testid="manage-standards">
                  {c.cp.manageStandards}
                </Button>
              </div>
            )}
            <input
              ref={standardInput}
              type="file"
              accept={STANDARD_AGREEMENT_ACCEPT}
              className="hidden"
              tabIndex={-1}
              data-testid="standard-file-input"
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = "";
                if (file) void uploadStandard(file);
              }}
            />
          </StepCard>

          {/* 2. The agreement we received */}
          <StepCard n={2} title={c.cp.step2Title}>
            <p className="-mt-2 text-sm text-muted-foreground">{c.cp.step2Label}</p>
            <div
              role="button"
              tabIndex={0}
              aria-label={c.cp.dropAria}
              data-testid="drop-zone"
              onClick={choose}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  choose();
                }
              }}
              {...handlers}
              className={cn(
                "flex min-h-[180px] cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-4 py-8 text-center transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 sm:px-6",
                dragging
                  ? "border-brand bg-brand-light ring-4 ring-brand/20"
                  : "border-muted-foreground/30 hover:border-brand/60 hover:bg-muted/40",
              )}
            >
              {dragging ? (
                <>
                  <Upload className="h-9 w-9 text-brand" />
                  <div className="text-lg font-semibold text-brand">{c.cp.dropRelease}</div>
                </>
              ) : source ? (
                <>
                  <FileText className="h-9 w-9 text-brand" />
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">{c.cp.chosenFile}</div>
                  <div className="max-w-full break-words text-lg font-semibold">{source.file.name}</div>
                  <div className="text-sm text-muted-foreground">
                    {source.kind === "upload" ? formatSize(source.file.size) : source.file.projectName}
                  </div>
                  <div className="mt-2 flex flex-wrap justify-center gap-2" onClick={(e) => e.stopPropagation()}>
                    <Button variant="outline" size="sm" onClick={choose}>
                      {c.cp.changeFile}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setSource(null)}>
                      <X className="mr-1 h-4 w-4" />
                      {c.cp.removeFile}
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <Upload className="h-9 w-9 text-muted-foreground" />
                  <div className="text-lg font-semibold">{c.cp.dropTitle}</div>
                  <div className="text-sm text-muted-foreground">{c.cp.dropHint}</div>
                  <div
                    className="mt-1 flex flex-wrap items-center justify-center gap-2 text-sm text-muted-foreground"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <span>{c.cp.orPick}</span>
                    <Button variant="outline" size="sm" onClick={() => setPickerOpen(true)}>
                      <FolderOpen className="mr-2 h-4 w-4" />
                      {c.cp.pickFromProject}
                    </Button>
                  </div>
                </>
              )}
            </div>
            <input
              ref={fileInput}
              type="file"
              accept={STANDARD_AGREEMENT_ACCEPT}
              className="hidden"
              tabIndex={-1}
              data-testid="file-input"
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = "";
                if (file) take(file);
              }}
            />
            {source && (
              <div className="-mt-1 flex justify-center">
                <Button variant="link" size="sm" onClick={() => setPickerOpen(true)}>
                  <FolderOpen className="mr-2 h-4 w-4" />
                  {c.cp.pickFromProject}
                </Button>
              </div>
            )}
            {lookalike && source && (
              <div
                role="alert"
                data-testid="lookalike-warning"
                className="space-y-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100"
              >
                <div className="flex items-start gap-2">
                  <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                  <p>{c.cp.looksLikeStandard(lookalike.name)}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {source.kind === "upload" && (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={uploadingStandard}
                      onClick={() => {
                        const file = source.file;
                        void uploadStandard(file).then((ok) => ok && setSource(null));
                      }}
                    >
                      {uploadingStandard ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
                      {c.cp.uploadAsStandard}
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => setIgnoredLookalike(source.file.name)}>
                    {c.cp.compareAnyway}
                  </Button>
                </div>
              </div>
            )}
          </StepCard>

          {/* 3. Our role and options */}
          <StepCard n={3} title={c.cp.step3Title}>
            <div className="max-w-sm min-w-0 space-y-2">
              <Label htmlFor="cmp-role">{c.cp.weAre}</Label>
              <Select value={role} onValueChange={setChosenRole}>
                <SelectTrigger id="cmp-role" data-testid="role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => (
                    <SelectItem key={r} value={r}>
                      {c.roles[r]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Collapsible open={optionsOpen} onOpenChange={setOptionsOpen}>
              <CollapsibleTrigger className="flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground">
                <ChevronDown className={cn("h-4 w-4 transition-transform", optionsOpen ? "" : "-rotate-90")} />
                {c.cp.options}
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-3 space-y-2">
                <Label htmlFor="cmp-focus">{c.focus}</Label>
                <Textarea
                  id="cmp-focus"
                  rows={3}
                  maxLength={8000}
                  value={focus}
                  placeholder={c.focusPlaceholder}
                  onChange={(e) => setFocus(e.target.value)}
                />
              </CollapsibleContent>
            </Collapsible>

            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-t pt-4">
              <Button size="lg" disabled={!source || !standardId || !!busy} onClick={() => void run()} data-testid="compare-button">
                {busy === "run" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <GitCompare className="mr-2 h-4 w-4" />}
                {busy === "run" ? c.starting : c.cp.run}
              </Button>
              {reason && (
                <span className="min-w-0 text-sm text-muted-foreground" role="status" data-testid="compare-reason">
                  {reason}
                </span>
              )}
            </div>
          </StepCard>
        </section>
      )}

      {/* Quick checks */}
      {view === "compare" && quickChecks.length > 0 && (
        <section aria-labelledby="quick-checks" className="space-y-3">
          <div>
            <h2 id="quick-checks" className="text-xl font-semibold">
              {c.cp.quickTitle}
            </h2>
            <p className="text-sm text-muted-foreground">{c.cp.quickSubtitle}</p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {quickChecks.map((check) => (
              <QuickCheckCard
                key={check.workflow.id}
                check={check}
                running={busy === check.workflow.id}
                locked={!!busy}
                onRun={(ch, file) => void runQuickCheck(ch, file)}
              />
            ))}
          </div>
        </section>
      )}

      {/* My comparisons */}
      {view === "compare" && (
        <section aria-labelledby="my-comparisons" className="space-y-3">
          <div>
            <h2 id="my-comparisons" className="flex items-center gap-2 text-xl font-semibold">
              {c.cp.mineTitle}
              <Lock className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
            </h2>
            <p className="text-sm text-muted-foreground">{c.cp.mineSubtitle}</p>
          </div>
          {items == null ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : items.length === 0 ? (
            <Card className="px-6 py-8 text-center text-sm text-muted-foreground" data-testid="no-comparisons">
              {c.cp.mineEmpty}
            </Card>
          ) : (
            <Card className="overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{c.received}</TableHead>
                    <TableHead className="hidden sm:table-cell">{c.standard}</TableHead>
                    <TableHead>{c.result}</TableHead>
                    <TableHead className="hidden text-right md:table-cell">{c.date}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visibleItems.map((item) => (
                    <TableRow
                      key={item.id}
                      className="cursor-pointer"
                      data-testid="my-comparison-row"
                      onClick={() => navigate(`/comparisons/${item.id}`)}
                    >
                      <TableCell className="max-w-[16rem]">
                        <a
                          href={`/comparisons/${item.id}`}
                          className="block break-words font-medium hover:underline"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            navigate(`/comparisons/${item.id}`);
                          }}
                        >
                          {item.receivedFileName}
                        </a>
                        <div className="text-xs text-muted-foreground sm:hidden">{item.standardName}</div>
                        <div className="text-xs text-muted-foreground md:hidden">{dateTime(item.createdAt)}</div>
                      </TableCell>
                      <TableCell className="hidden text-sm sm:table-cell">
                        {item.standardName}
                        {item.standardVersion && <span className="text-muted-foreground"> · {c.version(item.standardVersion)}</span>}
                      </TableCell>
                      <TableCell className="text-sm">
                        {item.status === "ready" && item.counts ? (
                          <div className="flex flex-wrap gap-1 text-xs font-medium">
                            {(["high", "medium", "low"] as const).map((r) => (
                              <span key={r} className={`rounded-full px-2 py-0.5 ${RISK_CHIP[r]}`}>
                                {c.risks[r]} {item.counts![r]}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span
                            className={cn(
                              "inline-flex items-center gap-1.5",
                              item.status === "failed" ? "text-red-600 dark:text-red-400" : "text-muted-foreground",
                            )}
                          >
                            {(item.status === "queued" || item.status === "processing") && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                            {c.statuses[item.status]}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="hidden whitespace-nowrap text-right text-sm text-muted-foreground md:table-cell">
                        {dateTime(item.createdAt)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {!showAll && items.length > INITIAL_ROWS && (
                <div className="border-t p-2 text-center">
                  <Button variant="ghost" size="sm" onClick={() => setShowAll(true)}>
                    {c.cp.showMore(items.length - INITIAL_ROWS)}
                  </Button>
                </div>
              )}
            </Card>
          )}
        </section>
      )}

      <ProjectFilePicker open={pickerOpen} onClose={() => setPickerOpen(false)} onPick={(file) => setSource({ kind: "project", file })} />
    </div>
  );
};

export default ComparePage;
